// Central promo config helper.
// Fetches /api/subscription/plans once, caches per_plan free_months and derives
// human-readable copy. All UI strings that mention "N mesi gratis" MUST route
// through this module so a single admin change (Admin → Prezzi piani → Mesi gratis)
// propagates everywhere without hardcoded numbers.
import api from "./api";

let cache = null;
let inflight = null;

/**
 * Fetches the promo config. Cached in memory for the session.
 * @param {boolean} force - bypass cache
 * @returns {Promise<{active:boolean, per_plan_months:Object, eligible:string[], max_months:number, any_promo:boolean, plans:Array}>}
 */
export async function getPromoConfig(force = false) {
    if (cache && !force) return cache;
    if (inflight && !force) return inflight;
    inflight = (async () => {
        try {
            const r = await api.get("/subscription/plans");
            const per = r.data?.promo?.per_plan_months || {};
            const eligible = Object.entries(per)
                .filter(([, m]) => Number(m) > 0)
                .map(([pid]) => pid);
            const max_months = Object.values(per).reduce((m, v) => Math.max(m, Number(v) || 0), 0);
            cache = {
                active: !!(r.data?.promo?.active) && max_months > 0,
                per_plan_months: per,
                eligible,
                max_months,
                any_promo: max_months > 0,
                plans: r.data?.plans || [],
            };
            return cache;
        } catch (e) {
            cache = { active: false, per_plan_months: {}, eligible: [], max_months: 0, any_promo: false, plans: [] };
            return cache;
        } finally {
            inflight = null;
        }
    })();
    return inflight;
}

/** Returns free_months for a plan id, 0 if none */
export function freeMonthsOf(config, planId) {
    return Number(config?.per_plan_months?.[planId] || 0);
}

/** Global CTA (navbar + hero "Per chi pubblica"): sempre "Iscriviti gratis →".
 *  L'informazione sui mesi di prova gratuita è mostrata SOLO nella pagina Prezzi (planCta/planPromoCaption). */
export function globalCta() {
    return "Iscriviti gratis →";
}

/** Global banner headline: "Promo lancio · fino a N mesi gratis" or null */
export function globalBanner(config) {
    if (!config?.any_promo) return null;
    return `Promo lancio · fino a ${config.max_months} mesi gratis`;
}

/** Per-plan CTA text on Pricing card */
export function planCta(config, planId, activating = false) {
    const m = freeMonthsOf(config, planId);
    if (activating) return "Attivazione...";
    if (m > 0) return `Attiva ${m} mes${m === 1 ? "e" : "i"} gratis →`;
    return "Attiva ora →";
}

/** Per-plan promo caption "per N mesi · poi €X,YZ/mese" — returns null if no promo */
export function planPromoCaption(config, planId, monthlyPrice) {
    const m = freeMonthsOf(config, planId);
    if (m <= 0) return null;
    const amount = Number(monthlyPrice || 0);
    const after = amount <= 0 ? "poi gratuito" : `poi €${amount.toFixed(2).replace(".", ",")}/mese`;
    return `per ${m} mes${m === 1 ? "e" : "i"} · ${after}`;
}

export function publicPriceLabel(value, included = false) {
    const amount = Number(value || 0);
    if (amount <= 0) return included ? "Incluso" : "Gratuito";
    return `€${amount.toFixed(2).replace(".", ",")}`;
}

/** Confirmation dialog before activating promo */
export function confirmActivate(config, planId, planName) {
    const m = freeMonthsOf(config, planId);
    if (m > 0) return `Attivare ${m} mes${m === 1 ? "e" : "i"} gratis sul piano ${planName || planId}?`;
    return `Attivare l'abbonamento al piano ${planName || planId}?`;
}

/** Chatbot suggested question — only shown if promo exists */
export function chatbotPromoQuestion(config) {
    if (!config?.any_promo) return null;
    return `Come funziona la promo ${config.max_months} mesi gratis?`;
}

/** Reset in-memory cache (call after admin changes plan) */
export function invalidatePromoCache() {
    cache = null;
    inflight = null;
}
